import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3141District, U3141Cadastre } from "../../../U3141Service";
import {
  buildConcordanceStringsFromRows,
  rowFromConcordanceEntry,
  validateConcordanceEntries,
  type U3141ConcordanceParentContext,
  type U3141ConcordanceRowModel,
} from "./U3141ConcordanceUtils";
import { useU3141FindPropertyDistrictsHandlers, useU3141FindCadastresHandlers, useU3141ResolveConcordanceHandlers } from "./U3141UseFindDistrictCadastreHandlers";
import { NS } from "../../../U3141";
import { FaCheck, FaPlus, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { setNestedValue } from "../../../../u2000Core/tools/U2000ObjectUtils";

export interface U3141ConcordanceRow extends U3141ConcordanceRowModel {
  /** Identifiant stable pour la clé React (survit aux suppressions de lignes). */
  clientRowId: string;
  district?: U3141District;
  cadastre?: U3141Cadastre;
}

export interface U3141ConcordanceSavePayload {
  concordance: string[];
  concordancePartieDeLot: boolean[];
}

export interface U3141ConcordanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  concordance: string[];
  /** Même index que concordance — prioritaire sur le parse (ptie) à l'ouverture. */
  concordancePartieDeLot?: boolean[];
  onSave: (payload: U3141ConcordanceSavePayload) => void;
  /** Contexte du lot parent (district/cadastre) pour résolution et rebuild des entrées courtes. */
  parentContext?: U3141ConcordanceParentContext;
}

/** Modèle local du dialogue (lignes de concordance). */
export type U3141ConcordanceFormModel = { rows: U3141ConcordanceRow[] };

function createConcordanceRowClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `conc-row-${crypto.randomUUID()}`;
  }
  return `conc-row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function withConcordanceRowClientId(row: Omit<U3141ConcordanceRow, 'clientRowId'>): U3141ConcordanceRow {
  return { ...row, clientRowId: createConcordanceRowClientId() };
}

function rowIndexFromFieldName(fieldName: string): number | null {
  const match = fieldName.match(/^rows\.(\d+)\./);
  if (!match) return null;
  const index = Number.parseInt(match[1], 10);
  return Number.isNaN(index) ? null : index;
}

function markRowDirty(rows: U3141ConcordanceRow[], index: number): void {
  if (rows[index]) {
    rows[index].dirty = true;
  }
}

function markChangedRowsDirty(prev: U3141ConcordanceFormModel | undefined, next: U3141ConcordanceFormModel): void {
  if (!prev?.rows || !next.rows) return;
  next.rows.forEach((row, index) => {
    const prevRow = prev.rows[index];
    if (!prevRow) {
      if ((row.lot ?? "").trim() !== "") {
        row.dirty = true;
      }
      return;
    }
    const prevSnapshot = JSON.stringify({ ...prevRow, dirty: undefined });
    const nextSnapshot = JSON.stringify({ ...row, dirty: undefined });
    if (prevSnapshot !== nextSnapshot) {
      row.dirty = true;
    } else {
      row.dirty = prevRow.dirty;
    }
  });
}

interface ConcordanceRowEditorProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3141ConcordanceFormModel>;
  findPropertyDistrictsHandlers: ReturnType<typeof useU3141FindPropertyDistrictsHandlers>;
  onDelete: () => void;
  canDelete: boolean;
}

function ConcordanceRowEditor({ index, formStateHandlers, findPropertyDistrictsHandlers, onDelete, canDelete }: ConcordanceRowEditorProps) {
  const { t } = useTranslation();
  const baseField = `rows.${index}`;
  const getDistrictId = useCallback(() => {
    const data = formStateHandlers.getData();
    const row = data?.rows?.[index];
    return row?.district?.id;
  }, [formStateHandlers, index]);

  const findCadastresForRow = useU3141FindCadastresHandlers({ getDistrictId });

  const onSearchDistrict = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      return findPropertyDistrictsHandlers.search(criterias);
    },
    [findPropertyDistrictsHandlers],
  );

  const onSearchCadastre = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      if (!getDistrictId()) return [];
      const result = await findCadastresForRow.search(criterias);
      return {
        ...result,
        results: result.results.map((r: Record<string, unknown>) => ({ value: r.id, ...r })),
      };
    },
    [findCadastresForRow, getDistrictId],
  );

  const extractDistrict = useCallback(
    (result: unknown) => {
      const d = result as U3141District | null | undefined;
      if (d) {
        return [
          { fieldName: `${baseField}.district`, value: d },
          { fieldName: `${baseField}.codeDistrict`, value: d.codeDistrict ?? null },
          { fieldName: `${baseField}.dirty`, value: true },
        ];
      }
      return [
        { fieldName: `${baseField}.district`, value: null },
        { fieldName: `${baseField}.codeDistrict`, value: null },
        { fieldName: `${baseField}.cadastre`, value: null },
        { fieldName: `${baseField}.cadastreCode`, value: null },
        { fieldName: `${baseField}.rankCode`, value: null },
        { fieldName: `${baseField}.rankLabel`, value: null },
        { fieldName: `${baseField}.dirty`, value: true },
      ];
    },
    [baseField],
  );

  const extractCadastre = useCallback(
    (result: unknown) => {
      const c = result as U3141Cadastre | null | undefined;
      if (c) {
        return [
          { fieldName: `${baseField}.cadastre`, value: c },
          { fieldName: `${baseField}.cadastreCode`, value: c.cadastreCode ?? null },
          { fieldName: `${baseField}.rankCode`, value: c.rankCode ?? null },
          { fieldName: `${baseField}.rankLabel`, value: c.rankLabel ?? null },
          { fieldName: `${baseField}.dirty`, value: true },
        ];
      }
      return [
        { fieldName: `${baseField}.cadastre`, value: null },
        { fieldName: `${baseField}.cadastreCode`, value: null },
        { fieldName: `${baseField}.rankCode`, value: null },
        { fieldName: `${baseField}.rankLabel`, value: null },
        { fieldName: `${baseField}.dirty`, value: true },
      ];
    },
    [baseField],
  );

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className="px-3 py-2 align-top min-w-[320px] w-[36%]">
        <U2000InputText
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.lot`}
          label=""
          readonly={false}
          required={false}
          inlineEnd={
            <U2000CheckBox
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.partieDeLot`}
              label={t("lblLotDetail.partieDeLot", { ns: NS })}
            />
          }
        />
      </td>
      <td className="px-3 py-2 align-top min-w-[200px] w-[24%]">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.district`}
          mainFieldName="propertyDistrict"
          label=""
          onSearch={onSearchDistrict}
          displayFn={(item: unknown) => {
            const d = item as Partial<U3141District> | null | undefined;
            return d?.propertyDistrict != null ? String(d.propertyDistrict) : "";
          }}
          overrideExtractDataFn={extractDistrict}
          basicResultHeight={300}
          resultLabels={[]}
          resultFields={["propertyDistrict"]}
        />
      </td>
      <td className="px-3 py-2 align-top min-w-[200px] w-[24%]">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.cadastre`}
          mainFieldName="cadastre"
          label=""
          onSearch={onSearchCadastre}
          displayFn={(item: unknown) => {
            const c = item as Partial<U3141Cadastre> | null | undefined;
            if (c?.cadastre == null) return "";
            return `${c.cadastre} ${c.rankLabel ?? ""}`.trimEnd();
          }}
          overrideExtractDataFn={extractCadastre}
          basicResultHeight={300}
          minCarLaunchSearch={2}
          resultLabels={[]}
          resultFields={["cadastre", "rankLabel"]}
          readonly={!getDistrictId()}
        />
      </td>
      <td className="px-3 py-2 align-top w-[10%] min-w-[88px] text-right">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-red-500"
            aria-label={t("btnDelete", { ns: NS })}
            title={t("btnDelete", { ns: NS })}
          >
            <FaTrash className="w-4 h-4" />
            <span className="sr-only">{t("btnDelete", { ns: NS })}</span>
          </button>
        )}
      </td>
    </tr>
  );
}

export default function U3141ConcordanceDialog({ isOpen, onClose, concordance, concordancePartieDeLot, onSave, parentContext }: U3141ConcordanceDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState<U3141ConcordanceFormModel>({ rows: [] });
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [resolving, setResolving] = useState(false);
  const wasOpenRef = useRef(false);

  const findPropertyDistrictsHandlers = useU3141FindPropertyDistrictsHandlers();
  const { resolveDistrict, resolveCadastre } = useU3141ResolveConcordanceHandlers();

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      setResolving(false);
      return;
    }

    const isReopening = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!isReopening) return;

    const rows: U3141ConcordanceRow[] = (concordance ?? []).map((entry, index) =>
      withConcordanceRowClientId(
        rowFromConcordanceEntry(entry, concordancePartieDeLot?.[index]) as unknown as Omit<U3141ConcordanceRow, 'clientRowId'>,
      ),
    );
    const initialRows: U3141ConcordanceRow[] =
      rows.length > 0
        ? rows
        : [withConcordanceRowClientId({ lot: '', partieDeLot: false, dirty: false, district: undefined, cadastre: undefined })];
    setData({ rows: initialRows });
    setResolving(true);

    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        initialRows.map(async (row): Promise<U3141ConcordanceRow> => {
          const hasDistrictId = row.district?.id != null && String(row.district.id).trim() !== "";
          const hasCadastreId = row.cadastre?.id != null && String(row.cadastre.id).trim() !== "";
          let district = row.district;
          const districtCode = row.codeDistrict ?? parentContext?.codeDistrict;
          const districtLabel = row.propertyDistrict ?? row.district?.propertyDistrict;
          if (!hasDistrictId && (districtCode || districtLabel)) {
            const resolvedDistrict = await resolveDistrict(districtCode ?? undefined, districtLabel ?? undefined);
            if (resolvedDistrict) district = resolvedDistrict;
          }
          if (!hasCadastreId && district?.id && (row.cadastreCode ?? row.cadastreLabel ?? row.cadastre?.cadastre ?? row.rankLabel)) {
            const resolvedCadastre = await resolveCadastre(
              String(district.id),
              row.cadastreCode ?? parentContext?.cadastreCode ?? row.cadastre?.cadastreCode,
              row.cadastreLabel ?? row.cadastre?.cadastre ?? row.rankLabel,
              row.rankCode ?? row.cadastre?.rankCode,
              row.rankLabel ?? row.cadastre?.rankLabel,
            );
            if (resolvedCadastre) {
              return { ...row, district, cadastre: resolvedCadastre };
            }
          }
          return { ...row, district };
        }),
      );
      if (!cancelled) {
        setData((prev) => ({
          rows: resolved.map((row, i) => {
            const prevRow = prev.rows[i];
            if (prevRow?.dirty) {
              return prevRow;
            }
            return {
              ...row,
              clientRowId: prevRow?.clientRowId ?? row.clientRowId,
              sourceEntry: prevRow?.sourceEntry ?? row.sourceEntry,
              dirty: prevRow?.dirty ?? false,
            };
          }),
        }));
        setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // resolveDistrict/resolveCadastre exclus des deps pour ne pas réinitialiser à chaque render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, concordance, concordancePartieDeLot, parentContext?.codeDistrict, parentContext?.cadastreCode]);

  const formStateHandlers = useMemo((): U2000FormStateHandlers<U3141ConcordanceFormModel> => {
    const getData = () => data;
    const setDataState = (upd: U3141ConcordanceFormModel) => setData((prev) => ({ ...prev, ...upd }));
    const onChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean | unknown) => {
      const target = _e?.target as HTMLInputElement | undefined;
      const fieldName = target?.id ?? target?.name;
      if (!fieldName) return;
      const current = getData();
      const updated = JSON.parse(JSON.stringify(current ?? {})) as U3141ConcordanceFormModel;
      setNestedValue(updated, fieldName, value);
      const rowIndex = rowIndexFromFieldName(fieldName);
      if (rowIndex != null) {
        markRowDirty(updated.rows, rowIndex);
      }
      setDataState(updated);
    };
    return {
      getData,
      setData: (upd: U3141ConcordanceFormModel) => {
        const prev = getData();
        markChangedRowsDirty(prev, upd);
        setData(upd);
      },
      getTouched: () => ({}),
      setTouched: () => {},
      getErrors: () => ({}),
      setErrors: () => {},
      getFormState: () => U2000FormStateEnumState.EDIT,
      isSubmitted: () => false,
      getOtherErrors: () => undefined,
      setOtherErrors: () => {},
      onChange,
    } as unknown as U2000FormStateHandlers<U3141ConcordanceFormModel>;
  }, [data]);

  const handleAddRow = useCallback(() => {
    setData((prev) => ({
      ...prev,
      rows: [...prev.rows, withConcordanceRowClientId({ lot: '', partieDeLot: false, dirty: false, district: undefined, cadastre: undefined })],
    }));
  }, []);

  const handleDeleteRow = useCallback((index: number) => {
    setDeleteIndex(index);
  }, []);

  const confirmDeleteRow = useCallback(() => {
    if (deleteIndex === null) return;
    setData((prev) => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== deleteIndex),
    }));
    setDeleteIndex(null);
  }, [deleteIndex]);

  const handleClose = useCallback(
    (action: "save" | "cancel") => {
      if (action === "save") {
        if (resolving) {
          toast.error(
            t("msgConcordancesResolving", { ns: NS, defaultValue: "Chargement des concordances en cours, veuillez patienter." }),
            t("lblLotDetail.concordances", { ns: NS }),
          );
          return;
        }
        const { strings, concordancePartieDeLot: partieDeLotFlags, errors: buildErrors } = buildConcordanceStringsFromRows(
          data.rows,
          parentContext,
        );
        if (buildErrors.length > 0) {
          buildErrors.forEach((err) => toast.error(err, t("lblLotDetail.concordances", { ns: NS })));
          return;
        }
        const validation = validateConcordanceEntries(strings);
        if (!validation.valid && validation.errors?.length) {
          validation.errors.forEach((err) => toast.error(err, t("lblLotDetail.concordances", { ns: NS })));
          return;
        }
        onSave({ concordance: strings, concordancePartieDeLot: partieDeLotFlags });
        toast.success(t("msgConcordancesSaved", { ns: NS, defaultValue: "Concordances enregistrées." }));
      }
      onClose();
    },
    [data.rows, onSave, onClose, toast, t, parentContext, resolving],
  );

  const isValid = !resolving;

  const emptyMessage = data.rows.length === 0 || (data.rows.length === 1 && !data.rows[0].lot?.trim());

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t("lblConcordanceDialogTitle", { ns: NS, defaultValue: "Modifier les concordances" })}
      width="x-large"
      height="max-h-[85vh]"
      isValid={isValid}
    >
      <div className="relative flex flex-col gap-4">
        {deleteIndex !== null && (
          <div
            className="absolute inset-0 z-100 flex items-center justify-center rounded-lg bg-black/30 backdrop-blur-[1px]"
            aria-modal="true"
            role="alertdialog"
            aria-labelledby="confirm-delete-title"
          >
            <div className="relative z-101 mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-surface p-5 shadow-lg">
              <h3 id="confirm-delete-title" className="text-lg font-semibold text-gray-900">
                {t("dlgConfirmDeleteConcordanceTitle", { ns: NS, defaultValue: "Supprimer cette concordance ?" })}
              </h3>
              <p className="mt-2 text-sm text-gray-600">{t("dlgConfirmDeleteConcordanceMessage", { ns: NS, defaultValue: "Voulez-vous supprimer cette entrée de la liste ?" })}</p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteIndex(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaTimesCircle className="h-4 w-4" />
                  {t("dlgLblNo", { defaultValue: "Non" })}
                </button>
                <button type="button" onClick={() => confirmDeleteRow()} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                  <FaCheck className="h-4 w-4" />
                  {t("dlgLblYes", { defaultValue: "Oui" })}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 bg-surface shadow-none">
          {resolving && (
            <p className="text-sm text-gray-600">
              {t("msgConcordancesResolving", { ns: NS, defaultValue: "Chargement des concordances en cours, veuillez patienter." })}
            </p>
          )}
          {emptyMessage && <p className="text-sm text-gray-600">{t("lblConcordanceEmptyHint", { ns: NS, defaultValue: "Aucune concordance. Cliquez sur Ajouter pour en ajouter une." })}</p>}
          <div className="overflow-x-auto overflow-y-auto max-h-[50vh] rounded-lg border border-gray-200 shadow-none">
            <table className="min-w-[980px] w-full text-sm table-fixed">
              <thead className="sticky top-0 bg-muted border-b border-gray-200 z-10 shadow-none">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 min-w-[320px] w-[36%]">{t("lblLotDetail.lot", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 min-w-[200px] w-[24%]">{t("lblLotDetail.propertyDistrict", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 min-w-[200px] w-[24%]">{t("lblLotDetail.cadastre", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-700 w-[10%] min-w-[88px]">
                    <button
                      type="button"
                      onClick={handleAddRow}
                      className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      aria-label={t("btnAdd", { ns: NS })}
                    >
                      <FaPlus className="w-4 h-4" />
                      <span>{t("btnAdd", { ns: NS })}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <ConcordanceRowEditor
                    key={row.clientRowId}
                    index={idx}
                    formStateHandlers={formStateHandlers}
                    findPropertyDistrictsHandlers={findPropertyDistrictsHandlers}
                    onDelete={() => handleDeleteRow(idx)}
                    canDelete
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}
